/** The `multispectral` kind's options (backend `viewer/report_kinds.py`, MULTISPECTRAL_OPTIONS) as the page reads them. */
import { BAND_ROLES, SPECTRAL_DISPLAYS, SPECTRAL_INDICES, type BandRole, type SpectralDisplay, type SpectralIndex } from '@/map/spectral';

export type MultispectralSection = 'map' | 'bands';
export const MULTISPECTRAL_SECTIONS: MultispectralSection[] = ['map', 'bands'];

/** Where each index's ramp starts and ends unless the report says otherwise (the backend's MULTISPECTRAL_STRETCH). */
export const DEFAULT_STRETCH: Record<SpectralIndex, [number, number]> = {
  ndvi: [0.35, 0.9],
  ndre: [0.13, 0.3],
  ngrdi: [-0.15, 0.39],
  gndvi: [0.3, 0.8],
};

export interface MultispectralConfig {
  sections: Record<MultispectralSection, { enabled: boolean }>;
  display: SpectralDisplay;
  band: BandRole;
  stretch: Record<SpectralIndex, [number, number]>;
}

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** A stored `{min, max}` over the default; an inverted one (the backend refuses it) falls back whole. */
function range(v: unknown, fallback: [number, number]): [number, number] {
  const stored = isObject(v) ? v : {};
  const lo = num(stored.min, fallback[0]);
  const hi = num(stored.max, fallback[1]);
  return lo < hi ? [lo, hi] : fallback;
}

/** The stored options over the defaults: a report saved before an option existed still has it. */
export function multispectralConfig(raw: unknown): MultispectralConfig {
  const stored = isObject(raw) ? raw : {};
  const sections = isObject(stored.sections) ? stored.sections : {};
  const stretch = isObject(stored.stretch) ? stored.stretch : {};
  return {
    sections: Object.fromEntries(
      MULTISPECTRAL_SECTIONS.map((s) => {
        const v = sections[s];
        return [s, { enabled: isObject(v) && typeof v.enabled === 'boolean' ? v.enabled : true }];
      }),
    ) as MultispectralConfig['sections'],
    display: (SPECTRAL_DISPLAYS as readonly unknown[]).includes(stored.display) ? (stored.display as SpectralDisplay) : 'ndvi',
    band: (BAND_ROLES as readonly unknown[]).includes(stored.band) ? (stored.band as BandRole) : 'nir',
    stretch: Object.fromEntries(SPECTRAL_INDICES.map((i) => [i, range(stretch[i], DEFAULT_STRETCH[i])])) as MultispectralConfig['stretch'],
  };
}

/**
 * What a file's band values are: reflectance when the tiler decodes them through the file's
 * scale (`unscaled`) or when they are stored as floats (ODM's own calibration); the stored
 * integers (DN) otherwise.
 */
export function valueUnit(meta: { dtype: string; unscaled?: boolean }): 'reflectance' | 'dn' {
  return meta.unscaled || meta.dtype.startsWith('float') ? 'reflectance' : 'dn';
}

/** Decimals a band value is shown with, by what it is. */
export const UNIT_DIGITS = { reflectance: 3, dn: 0 } as const;
